// src/utils/aiContext.js
import axios from 'axios';

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL;
const WEBINARS_CACHE_MS = 5 * 60 * 1000;
const UPCOMING_WEBINARS = 3;
const RECENT_RECORDINGS = 5;

const PAGE_CONTEXTS = {
  // ===== PUBLIEKE PAGINA'S =====
  '/': 'LANDINGSPAGINA: Doel is nieuwe bezoekers informeren en laten registreren. Bevat introductievideo en voordelen.',
  '/reset-password': 'WACHTWOORD RESETTEN: Gebruiker stelt nieuw wachtwoord in.',
  '/verify-email': 'EMAIL VERIFICATIE: Bevestiging van accountregistratie.',

  // ===== DASHBOARD & PROFIEL =====
  '/dashboard': 'DASHBOARD: Startpunt. Links: AI-assistent Doortje. Rechts: Voortgang, Uitgelicht, Nieuws. Hier stelt de leraar startvragen.',
  '/profile': 'PROFIEL: Beheer van persoonsgegevens, rol in onderwijs, school en wachtwoord.',
  '/cluster-builder-test': 'CLUSTERBOUWER: Tool om visueel lesplannen/thema\'s te maken met hexagonen (Doorloopjes, Waarom, Hoe).',

  // ===== CONTENT OVERZICHTEN =====
  '/doorloopjes': 'DOORLOOPJES OVERZICHT: Alle technieken, gefilterd op boek, thema of status. Kern van het platform.',
  '/webinars': 'WEBINARS (DIGIDATES): Overzicht van aankomende webinars en opnames voor verdieping.',
  '/achtergronden': 'KENNISBANK: Achtergrondartikelen, theorie (Waarom) en praktische uitvoering (Hoe).',
  '/nieuws': 'NIEUWS: Updates en inspiratie over het platform.',

  // ===== VIDEO ANALYSE =====
  '/video-analyse': 'VIDEO ANALYSE OVERZICHT: Dashboard met alle video-analyses van de gebruiker. Hier kan de gebruiker een nieuwe analyse starten of bestaande rapporten bekijken.',
  '/video-analyse/nieuw': 'NIEUWE VIDEO ANALYSE: Gebruiker kiest een doorloopje, selecteert eventueel specifieke stappen, uploadt een lesvideo en start de AI-analyse.',
  '/video-analyse/:documentId': 'VIDEO ANALYSE STATUS: Wachtpagina terwijl de video wordt verwerkt door de AI.',
  '/video-analyse/:documentId/rapport': 'VIDEO ANALYSE RAPPORT: Resultaten van de AI-analyse. Bevat een persoonlijke inleiding, per stap observaties, sterke punten, verbeterpunten en acties. De gebruiker kan de video terugkijken, notities maken en de doorloopje-stappen raadplegen.',

  // ===== CONTENT DETAILS (Generic patterns) =====
  '/doorloopjes/:slug': 'DOORLOOPJE DETAIL: Praktische stapsgewijze uitleg van een techniek. Bevat 5 stappen, video, en status-knoppen (start/afronden).',
  '/webinars/:slug': 'WEBINAR DETAIL: Inschrijven voor aankomend webinar of opname terugkijken.',
  '/why/:slug': 'WAAROM DETAIL: Theoretische onderbouwing van een techniek.',
  '/how/:slug': 'HOE DETAIL: Praktische instructies voor uitvoering.',
  '/artikel/:slug': 'ARTIKEL DETAIL: Verdiepend blogartikel.',
  '/nieuws-item/:slug': 'NIEUWS ITEM: Specifiek nieuwsbericht.',
  '/voorbeeld/:slug': 'VOORBEELD: Publiek toegankelijk voorbeeld van een doorloopje (voor niet-ingelogde gebruikers).',
};

// Helper om path patterns te matchen (bijv /doorloopjes/de-start)
function getPageDescription(pathname) {
  if (PAGE_CONTEXTS[pathname]) return PAGE_CONTEXTS[pathname];
  
  // Check patterns like /doorloopjes/:slug
  for (const pattern of Object.keys(PAGE_CONTEXTS)) {
    if (pattern.includes(':')) {
      const regex = new RegExp(`^${pattern.replace(/:[^\s/]+/g, '[^/]+')}$`);
      if (regex.test(pathname)) {
        return PAGE_CONTEXTS[pattern];
      }
    }
  }
  return 'Onbekende pagina';
}

// Wat een bezoeker zelf heeft (zijn gesprekken, zijn naam) draagt dit kenmerk en gaat
// niet mee als paginatekst: die tekst is wat de site zelf zegt.
const EIGEN_GEGEVENS = '[data-eigen-gegevens]';

/**
 * Haalt de zichtbare tekst van de pagina op (DOM scraping), zonder de elementen
 * met data-eigen-gegevens.
 */
export function getPageText() {
  if (typeof document === 'undefined') return '';

  try {
    // Probeer eerst de specifieke content wrapper, anders main, anders body
    const main =
      document.querySelector('.content-scroll-wrapper') ||
      document.querySelector('main') ||
      document.body;

    if (!main) return '';

    const kopie = main.cloneNode(true);
    kopie.querySelectorAll(EIGEN_GEGEVENS).forEach((el) => el.remove());
    return (kopie.textContent || '').replace(/\s+/g, ' ').trim();
  } catch {
    return '';
  }
}

// Lokale kalenderdatum als 'YYYY-MM-DD', vergelijkbaar met event_date van Strapi
function isoDate(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function formatDutchDate(date, withWeekday) {
  return date.toLocaleDateString('nl-NL', {
    ...(withWeekday && { weekday: 'long' }),
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function formatEventDate(eventDate) {
  const [year, month, day] = eventDate.split('-').map(Number);
  return formatDutchDate(new Date(year, month - 1, day), true);
}

/**
 * Kiest de eerstvolgende webinars (vanaf vandaag) en de meest recente opnames
 * (voor vandaag, alleen met recording_url), op event_date en niet op has_taken_place.
 */
export function pickWebinars(digidates, today) {
  const todayIso = isoDate(today);
  const dated = digidates.filter((d) => d.event_date);
  const upcoming = dated
    .filter((d) => d.event_date >= todayIso)
    .sort((a, b) => a.event_date.localeCompare(b.event_date))
    .slice(0, UPCOMING_WEBINARS);
  const recordings = dated
    .filter((d) => d.event_date < todayIso && d.recording_url)
    .sort((a, b) => b.event_date.localeCompare(a.event_date))
    .slice(0, RECENT_RECORDINGS);
  return { upcoming, recordings };
}

export function formatWebinarsSection({ upcoming, recordings }, origin) {
  const line = (d) => `- ${d.title.replace(/\s+/g, ' ').trim()} (${formatEventDate(d.event_date)}): ${origin}/webinars/${d.slug}`;
  const parts = [];
  if (upcoming.length) parts.push(`Aankomend:\n${upcoming.map(line).join('\n')}`);
  if (recordings.length) parts.push(`Opnames terugkijken:\n${recordings.map(line).join('\n')}`);
  return parts.length ? `\n\n**Webinars (DigiDates):**\n${parts.join('\n')}` : '';
}

let webinarsCache = { at: 0, digidates: null };

// Gepubliceerde digidates, een paar minuten gecachet; null als het ophalen mislukt
async function fetchDigidates() {
  if (webinarsCache.digidates && Date.now() - webinarsCache.at < WEBINARS_CACHE_MS) {
    return webinarsCache.digidates;
  }
  try {
    const jwt = localStorage.getItem('jwt');
    const response = await axios.get(
      `${STRAPI_URL}/api/digidates?sort=event_date:desc&fields[0]=title&fields[1]=slug&fields[2]=event_date&fields[3]=recording_url&pagination[pageSize]=100`,
      { headers: { Authorization: `Bearer ${jwt}` } }
    );
    webinarsCache = { at: Date.now(), digidates: response.data.data };
    return webinarsCache.digidates;
  } catch (err) {
    console.error('Fout bij ophalen webinars voor AI-context:', err);
    return null;
  }
}

/**
 * Genereert de context string voor de AI.
 * Wordt aangeroepen vlak voordat een bericht wordt verstuurd.
 */
export async function generateAiContext({ user, location }) {
  const url = window.location.href;
  const title = document.title;
  const pathname = location?.pathname || window.location.pathname;
  
  // 1. User Profile
  const profileLines = [];
  if (user) {
    if (user.fullName) profileLines.push(`Naam: ${user.fullName}`);
    if (user.educationRole) profileLines.push(`Rol: ${user.educationRole}`);
    if (user.educationSector) profileLines.push(`Sector: ${user.educationSector}`);
    if (user.schoolName) profileLines.push(`School: ${user.schoolName}`);
  } else {
    profileLines.push('Gebruiker is niet ingelogd (gast).');
  }

  // Datum en webinars staan vóór de paginatekst, zodat het afkappen aan het eind ze nooit raakt
  const today = new Date();
  const dateLine = `Vandaag: ${formatDutchDate(today, true)}`;
  const digidates = await fetchDigidates();
  const webinarsSection = digidates
    ? formatWebinarsSection(pickWebinars(digidates, today), window.location.origin)
    : '';

  // 2. Progress / Lists (Bezig, Afgerond, Opgeslagen)
  // GEEN LIMIET: Stuurt alle items mee
  const formatList = (list) => (list || []).map(d => `- ${d.title}`).join('\n');
  
  let listsSection = '';
  if (user) {
    const inProgress = formatList(user.inprogress_doorloopjes);
    const completed = formatList(user.completed_doorloopjes);
    const saved = formatList(user.favorite_doorloopjes); // [HERSTELD]
    
    if (inProgress) listsSection += `\n\n**Bezig met:**\n${inProgress}`;
    if (completed) listsSection += `\n\n**Afgerond:**\n${completed}`;
    if (saved) listsSection += `\n\n**Opgeslagen (Favorieten):**\n${saved}`; // [HERSTELD]
  }

  // 3. Page Context (URL beschrijving)
  const pageDesc = getPageDescription(pathname);
  const pageSection = `\n\n**Pagina Context:**\n- URL: ${url}\n- Titel: ${title}\n- Type: ${pageDesc}`;

  // 4. Page Content (Live DOM tekst) [HERSTELD & UNLIMITED]
  const pageText = getPageText();
  const pageTextSection = pageText ? `\n\n**Pagina Inhoud (Live tekst):**\n${pageText}` : '';

  // Combineer alles
  return `${dateLine}\n\n**Gebruikersprofiel:**\n${profileLines.join('\n')}${webinarsSection}${listsSection}${pageSection}${pageTextSection}`;
}